import axios from 'axios'
import { withoutEmptyHeaders } from '~/common/http-headers'
import { nanoid } from 'nanoid'
import { captureException, handleLoadReCaptcha } from '~/common/helpers'
import { action, computed, observable, persist, storage, store, when } from '~/common/mobx.decorator'
import { authStore } from './auth.store'
import { THEMES } from '~/themes'
import { apolloClient, GET_APP_INIT_QUERY, GET_USER_INFO_QUERY } from '~/common/apollo'
import { COMPANY_CODE } from '~/common/constants/company.constant'
import { PATHS } from '~/common/constants/path.constant'

axios.interceptors.response.use(undefined, (error) => {
	captureException('Axios Client', error)
	return Promise.reject(error)
})

// TEMPORARY — `maestro_global_search` is not served by appInit yet, so the flag
// would read false on every tenant and the new search would be unreachable.
// Forced on so it can be exercised. Set this back to false the moment BE ships
// the flag; `enableGlobalSearch` below already reads it correctly and needs no
// other change.
const FORCE_GLOBAL_SEARCH = true

// Local-only escape hatch for the Shift Jobs tab, whose BE flag
// (`maestro_shift_jobs_tab`) does not exist yet. MUST stay false on every
// branch that ships — turning it on splits per diem out of Available Jobs for
// every tenant, flag or no flag.
const FORCE_SHIFT_JOBS_TAB = false

// Tenant flag `maestro_v2_release`. Absence means off, so a tenant stays on
// Classic until the flag is served and enabled. A tenant that sends the flag
// still wins: `enabled: true` turns V2 on, `enabled: false` keeps it off.
const MAESTRO_V2_ON_WHEN_UNSERVED = false

@store({ persist: true })
class AppStore {
	constructor() {
		storage.ready(() => {
			this.ready = true
			if (!this._uniqueId) {
				this._uniqueId = nanoid()
			}
		})
	}

	@persist @observable _uniqueId
	@observable config = null
	@observable ready = false

	@observable liveChatId
	@observable meta_domain

	@observable appConfig
	@observable appPublicConfig
	// Runtime (deploy-time) config served from /app-config.json — see fetchFEConfig.
	@observable feConfig

	@observable tenantId = ''
	@observable apiUrl = ''
	@observable themeConfig = null
	@observable recaptchaLoaded = false

	@action
	setTenantId = (tenantId) => {
		this.tenantId = tenantId
		sessionStorage.setItem('tenantId', tenantId)
	}

	@action
	setApiUrl = (apiUrl) => {
		this.apiUrl = apiUrl
		sessionStorage.setItem('apiUrl', apiUrl)
		axios.defaults.baseURL = `${apiUrl}/api/public/v1`
	}

	@action
	setThemeConfig = (themeConfig) => {
		this.themeConfig = themeConfig

		// Set global config for recaptcha access without circular import
		if (!window.__OPUS_APP_CONFIG__) {
			window.__OPUS_APP_CONFIG__ = {}
		}

		const newKey = themeConfig?.config?.REACT_APP_RECAPTCHA_KEY
		const oldKey = window.__OPUS_APP_CONFIG__.recaptchaKey

		window.__OPUS_APP_CONFIG__.recaptchaKey = newKey
		window.__OPUS_APP_CONFIG__.apiReady = true

		// Trigger reload of reCAPTCHA script if key changed and already loaded
		if (typeof window !== 'undefined' && oldKey && oldKey !== newKey && this.recaptchaLoaded) {
			const existingScript = document.getElementById('recaptcha-key')
			if (existingScript) {
				existingScript.remove()
				this.recaptchaLoaded = false // Allow reload
			}
		}

		// Auto-load reCAPTCHA when config is set
		this.tryLoadReCaptcha()
	}

	/*
	 * reCAPTCHA is only ever executed from the auth screens (sign-in, reset,
	 * change-password) and from the profile OTP action — but this ran on every
	 * tenant-config set, so all 313KB of `recaptcha/api.js` was fetched and
	 * executed on the dashboard, the matches tables and every other authenticated
	 * screen. Lighthouse measured 171KB of it unused there.
	 *
	 * Preloading still happens where it buys something: on an auth screen, so the
	 * first sign-in press does not wait on the script. Everywhere else
	 * `executeRecaptcha` loads it on demand — it already had that path
	 * (`if (!document.getElementById('recaptcha-key')) await handleLoadReCaptcha()`),
	 * which is what the profile OTP flow will now use.
	 */
	get isOnAuthScreen() {
		const path = (window.location?.pathname || '').toLowerCase()
		return [PATHS.common.signin, PATHS.common.reset, PATHS.common.change_password].some((p) => path.startsWith(p))
	}

	@action
	tryLoadReCaptcha = async () => {
		if (this.isApiConfigReady && !this.recaptchaLoaded && this.isOnAuthScreen) {
			try {
				await this.loadReCaptcha()
			} catch (error) {
				// Silent fail - will retry when needed
			}
		}
	}

	@action
	loadReCaptcha = async () => {
		if (this.recaptchaLoaded) return

		this.recaptchaLoaded = true
		try {
			await handleLoadReCaptcha()
		} catch (error) {
			this.recaptchaLoaded = false // Reset on error to allow retry
			throw error
		}
	}

	@computed
	get hideHotJobCandidateSetting() {
		return this.code === COMPANY_CODE.lsr || this.code === COMPANY_CODE.crossmed || this.code === COMPANY_CODE.concentric
	}

	@computed
	get recaptchaKey() {
		return this.themeConfig?.config?.REACT_APP_RECAPTCHA_KEY || process.env.REACT_APP_RECAPTCHA_KEY
	}

	@computed
	get isRecaptchaReady() {
		return this.recaptchaLoaded && !!document.getElementById('recaptcha-key')
	}

	@computed
	get isApiConfigReady() {
		// API config is ready when fetchTenantConfig completed
		// This is confirmed by having apiUrl set AND either themeConfig OR apiReady flag
		const hasApiUrl = this.apiUrl !== ''
		const hasThemeConfig = !!this.themeConfig
		const apiMarkedReady = window.__OPUS_APP_CONFIG__?.apiReady === true

		return hasApiUrl && (hasThemeConfig || apiMarkedReady)
	}

	@computed
	get isEnableAA() {
		return this.appConfig?.tenantConfig?.enableOpusAiAssistant || false
	}

	/**
	 * Whether the Opus AI assistant exists for this tenant at all — the widget
	 * AND the header button that opens it.
	 *
	 * One computed, deliberately, because the two used to decide separately and
	 * disagreed: `app.js` gated mounting `<Assistant />` on the tenant flag while
	 * the "Ask AI" button in `breadcrumbs.component.js` rendered unconditionally.
	 * On a tenant with `enableOpusAiAssistant` off, that shipped a prominent
	 * gradient button which did nothing at all when pressed — `window.OpusAssistant`
	 * is never defined, so its onClick short-circuits in silence.
	 *
	 * Anything that shows an entry point to the assistant reads THIS.
	 */
	@computed
	get showOpusAssistant() {
		// The env override stays a local/dev escape hatch so the assistant can be
		// exercised without flipping a tenant flag; it is not set in any build.
		return this.isEnableAA || process.env.REACT_APP_ENABLE_AIA === 'true'
	}

	@computed
	get tenantConfig() {
		return this.appConfig.tenantConfig || {}
	}

	// Per-tenant minimum matching threshold for the Matches tab (appInit.tenantConfig.matchesMinScore).
	// `null`/`0` mean "no threshold". BE applies it to the main matches list server-side; FE applies it
	// defensively to the inner matched-jobs/candidates popups. See docs/FE_matches_min_score.md.
	@computed
	get matchesMinScore() {
		const value = Number(this.appConfig?.tenantConfig?.matchesMinScore)
		return Number.isFinite(value) && value > 0 ? value : 0
	}

	@computed
	get enableMatchesFeedback() {
		if (this.appConfig) {
			const enableMatchesFeedback = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_enable_matches_feedback')
			if (enableMatchesFeedback && enableMatchesFeedback.enabled) {
				return true
			}
		}
		return false
	}

	// Gates the whole 1099 locum pay work in Maestro: the provider's Pay Matrix
	// (earnings by facility and shift type), timecard submission, and the
	// recruiter's timecard approval queue.
	//
	// Off by default, and off means the routes are never mounted — not merely
	// hidden. Every tenant on Maestro today keeps exactly the navigation it has,
	// because a tenant that does not do 1099 locum work has no timecards to show
	// and an empty Pay Matrix would read as a broken screen rather than an
	// unused one.
	@computed
	get enableOpusMatchPay() {
		if (this.appConfig) {
			const feature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_enable_opus_match_pay')
			if (feature && feature.enabled) {
				return true
			}
		}
		return false
	}

	// Recruiter ↔ candidate messaging on the in-app-message service. Off means the
	// route, the nav entry and the socket do not exist. The tenant also needs an
	// in-app-message integration on maestro-api — without one the token comes back
	// empty and the Messages page says messaging is unavailable.
	@computed
	get enableInAppMessage() {
		if (!process.env.REACT_APP_IN_APP_MESSAGE_URL) {
			return false
		}
		const feature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_enable_in_app_message')
		return Boolean(feature?.enabled)
	}

	// SPR-571: gates the entire Maestro Matches filter-enhancements work (config-driven
	// filter engine — new columns, toolbar/presets/saved-views/breakdown, enhancement
	// filter serialization). When OFF the Matches tabs behave exactly as before this work.
	@computed
	get enableMatchesFilterEnhancements() {
		if (this.appConfig) {
			const feature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_matches_filter_enhancements')
			if (feature && feature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableUpcomingWorkerAssignments() {
		if (this.appConfig) {
			const enableUpcomingWorkerAssignments = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_upcoming_worker_assignments')
			if (enableUpcomingWorkerAssignments && enableUpcomingWorkerAssignments.enabled) {
				return true
			}
		}
		return false
	}

	/**
	 * Splits per-diem (shift) jobs out of Jobs › Available Jobs into their own
	 * "Shift Jobs" tab. While it is off nothing about the Jobs page changes:
	 * Available Jobs keeps listing every open job, per diem included.
	 *
	 * ⚠️ BE contract — the tenant flag is `maestro_shift_jobs_tab`. It is not
	 * served by appInit yet; until it is, flip FORCE_SHIFT_JOBS_TAB above to
	 * exercise the tab locally.
	 */
	@computed
	get enableShiftJobsTab() {
		if (FORCE_SHIFT_JOBS_TAB) {
			return true
		}
		if (this.appConfig) {
			const shiftJobsFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_shift_jobs_tab')
			if (shiftJobsFeature && shiftJobsFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableReportAndAnalytics() {
		if (this.appConfig) {
			const reportAndAnalyticsFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'reports_and_analytics')
			if (reportAndAnalyticsFeature && reportAndAnalyticsFeature.enabled) {
				return true
			}
		}
		return false
	}

	/**
	 * Maestro V2 (beta) — the redesigned Today / Jobs / Candidates screens under
	 * /v2, running beside V1. With the flag off /v2 is never registered and the
	 * V1 <-> V2 switch is not drawn.
	 *
	 * BE contract — tenant flag `maestro_v2_release`. A tenant that says something wins;
	 * only a tenant that says nothing gets MAESTRO_V2_ON_WHEN_UNSERVED.
	 */
	@computed
	get enableMaestroV2() {
		const maestroV2Feature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_v2_release')
		if (maestroV2Feature) {
			return Boolean(maestroV2Feature.enabled)
		}
		return MAESTRO_V2_ON_WHEN_UNSERVED
	}

	/**
	 * Pipeline blockers under /v2/pipeline. Off unless the tenant explicitly
	 * enables `maestro_job_applicant_tracking` — no nav entry and the route
	 * redirects to Today.
	 */
	@computed
	get enableMaestroJobApplicantTracking() {
		if (this.appConfig) {
			const flag = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_job_applicant_tracking')
			if (flag?.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableViewAsCandidate() {
		if (this.appConfig) {
			const viewAsCandidateFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'view_as_candidate')
			if (viewAsCandidateFeature && viewAsCandidateFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get candidatePortalUrl() {
		// Candidate Portal web URL for the "View as Candidate" iframe — sourced from
		// tenantConfig.ssrUrl (the direct web origin). NOTE: candidateUrl can be a
		// Branch deep link that drops the /impersonate path, so ssrUrl is used instead.
		// Used as the iframe-src base + postMessage origin allowlist.
		return this.appConfig?.tenantConfig?.ssrUrl || ''
	}

	@computed
	get enableEmployerImpersonation() {
		if (this.appConfig) {
			const impersonationFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'employer_impersonation_enabled')
			if (impersonationFeature && impersonationFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableEmployeeLogout() {
		if (this.appConfig) {
			const employeeLogoutFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'employer_logout')
			if (employeeLogoutFeature && employeeLogoutFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableDeleteResume() {
		if (this.appConfig) {
			const deleteResumeFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'delete_resume')
			if (deleteResumeFeature && deleteResumeFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableWorkerDisciplineSpecialties() {
		if (this.appConfig?.tenantConfig) {
			return this.appConfig?.tenantConfig?.specialtyAssociateWithDiscipline
		}
		return false
	}

	@computed
	get enableChangeEmployeeRole() {
		if (this.appConfig) {
			const changeEmployeeRoleFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'change_employer_role')
			if (changeEmployeeRoleFeature && changeEmployeeRoleFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableSystemNotificationSettings() {
		if (this.appConfig) {
			const systemNotificationSettingsFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'system_setting_notifications')
			if (systemNotificationSettingsFeature && systemNotificationSettingsFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableProfileCompletion() {
		if (this.appConfig) {
			const profileCompletionFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'profile_completion')
			if (profileCompletionFeature && profileCompletionFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enablePerDiemEligible() {
		if (this.appConfig) {
			const perDiemEligibleFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_per_diem_eligible_worker')
			if (perDiemEligibleFeature && perDiemEligibleFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableStateLicenseColumn() {
		// if (this.appConfig) {
		// 	const stateLicenseColumnFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_state_license_column')
		// 	if (stateLicenseColumnFeature && stateLicenseColumnFeature.enabled) {
		// 		return true
		// 	}
		// }
		// return false
		return true
	}

	@computed
	get enableSelfMode() {
		if (this.appConfig) {
			const selfModeFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_self_mode')
			if (selfModeFeature && selfModeFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get showBillRate() {
		if (this.appConfig) {
			const billRateFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_show_bill_rate')
			if (billRateFeature && billRateFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get showAtsSource() {
		if (this.appConfig) {
			const feature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_show_ats_source')
			if (feature && feature.enabled) {
				return true
			}
		}
		return false
	}

	// SPT-2090 — Staffing Specialist column (worker.secondEmployer) next to
	// Assigned Recruiter. Han Staff only for now: second_employer is sparse or
	// unused on other tenants, where the column would always be empty.
	@computed
	get showStaffingSpecialist() {
		if (this.appConfig) {
			const feature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_show_staffing_specialist')
			if (feature && feature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get showMarginPercentage() {
		if (this.appConfig) {
			const marginFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_show_margin_percentage')
			if (marginFeature && marginFeature.enabled) {
				return true
			}
		}
		return false
	}
	@computed
	get enableWorkflowGuide() {
		if (this.appConfig) {
			const workflowGuideFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_workflow_guide')
			if (workflowGuideFeature && workflowGuideFeature.enabled) {
				return true
			}
		}
		return false
	}

	/**
	 * Add user on the User Roles page.
	 *
	 * Off unless the tenant has opted in. `createMaestroUser` ships in the shared
	 * Maestro gem, so it reaches every tenant's schema on deploy; the backend gates
	 * it on this same flag and rejects the call outright when it is off. This
	 * computed only decides whether to show the button, so an admin at a tenant that
	 * has not opted in does not click into an error.
	 */
	@computed
	get enableCreateMaestroUser() {
		if (this.appConfig) {
			const createUserFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_create_local_user')
			if (createUserFeature && createUserFeature.enabled) {
				return true
			}
		}
		return false
	}

	/**
	 * Maestro global search (type-ahead + results page).
	 *
	 * Gates the rewrite: off, the legacy per-page dropdowns stay exactly as they
	 * were. Enable per tenant only once the Maestro engine exposes
	 * `globalSearch` / `globalSearchSuggestions` (gem ≥ 0.1.7 for the full
	 * field set).
	 *
	 * Currently short-circuited by FORCE_GLOBAL_SEARCH — see the note at the top
	 * of this file.
	 */
	@computed
	get enableGlobalSearch() {
		if (FORCE_GLOBAL_SEARCH) {
			return true
		}
		if (this.appConfig) {
			const globalSearchFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_global_search')
			if (globalSearchFeature && globalSearchFeature.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableCandidateLifecycle() {
		if (this.appConfig) {
			const lifecycleFeature = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_candidate_lifecycle_timeline')
			if (lifecycleFeature && lifecycleFeature.enabled) {
				return true
			}
		}
		return false
	}

	/*
	 * HS-487 Recent Applications tab on Recruiter Home. Behind
	 * `maestro_recent_applications_tab` so it only shows for tenants that asked for
	 * it, and only once the Maestro engine serves `appliedDatetime`.
	 */
	@computed
	get enableRecentApplicationsTab() {
		if (this.appConfig) {
			const flag = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_recent_applications_tab')
			if (flag?.enabled) {
				return true
			}
		}
		return false
	}

	@computed
	get enableCandidateAppAccess() {
		if (this.appConfig) {
			const flag = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_candidate_app_access')
			if (flag?.enabled) {
				return true
			}
		}
		return false
	}

	/**
	 * SPT-2326 (PH-289 follow-up) — View Applicants shows where an application came from:
	 * Source / Creator / Campaign columns, a Creator filter and the CSV export.
	 * Tenant flag `maestro_applicant_source_tracking`, Pioneer only at first. Off means the
	 * API also answers `applicationTracking: null` and no `creators` facet.
	 */
	@computed
	get enableApplicantSourceTracking() {
		if (this.appConfig) {
			const flag = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_applicant_source_tracking')
			if (flag?.enabled) {
				return true
			}
		}
		return false
	}

	/**
	 * Per-site kill switch for email workflows, read from /app-config.json at boot.
	 *
	 * One FE build serves every tenant, but not every backend runs the same gem
	 * branch: Pioneer is on `master`, GHR/core-api is on `ghr-master`, which has
	 * no cadence schema. The backend flag is the normal gate; this switch is the
	 * lever a releaser can pull per site, without a rebuild, if a shared document
	 * ever starts selecting a field the older gem does not know.
	 */
	@computed
	get disableEmailWorkflows() {
		return this.feConfig?.disableEmailWorkflows === true
	}

	// SPT-2164 Email workflows + Email templates. Per-tenant rollout: the hub cards,
	// the /cadences and /email-templates routes and every cadence query key off this.
	// The Configuration hub itself, User Roles and Candidate App Access do not.
	//
	// The runtime kill switch ANDs with the backend flag: it can only ever turn the
	// feature OFF, never on. A site with `disableEmailWorkflows: true` stays dark
	// even when the tenant has `maestro_email_workflows` enabled.
	@computed
	get enableEmailWorkflows() {
		if (this.disableEmailWorkflows) {
			return false
		}

		if (this.appConfig) {
			const flag = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_email_workflows')
			if (flag?.enabled) {
				return true
			}
		}
		return false
	}

	// Same lever as disableEmailWorkflows, for the notification settings hub: AND-only, it can turn the
	// feature off on one site (an older gem branch) without a rebuild, never on.
	@computed
	get disableNotificationSettings() {
		return this.feConfig?.disableNotificationSettings === true
	}

	// Notification settings hub (Configuration > Notifications). Per-tenant rollout through
	// `maestro_notification_settings`, independent of `maestro_email_workflows`. A missing flag is off,
	// and no notification query is sent while it is off.
	@computed
	get enableNotificationSettings() {
		if (this.disableNotificationSettings) {
			return false
		}
		const flag = this.appConfig?.featureFlags?.find((feature) => feature.feature === 'maestro_notification_settings')
		return flag?.enabled === true
	}

	/* Company Id  */
	@computed
	get id() {
		return process.env.REACT_APP_COMPANY_ID
	}

	@computed
	get name() {
		return this.appConfig?.tenant?.name || process.env.REACT_APP_COMPANY_NAME
	}

	@computed
	get code() {
		return this.appConfig?.tenant?.tenantCode
	}

	@computed
	get logo() {
		if (this.appConfig?.tenant?.logo?.file?.fileUrl) {
			return this.appConfig?.tenant?.logo?.file?.fileUrl
		}
		return `${process.env.PUBLIC_URL}/assets/defaultLogo.png`
	}

	@computed
	get publicLogo() {
		if (this.appPublicConfig?.tenant?.logo?.file?.fileUrl) {
			return this.appPublicConfig?.tenant?.logo?.file?.fileUrl
		}
		return this.logos.logoClient
	}

	@computed
	get logos() {
		return {
			logo: this.logo,
			logoClient: `${process.env.PUBLIC_URL}/assets/logoClient.png?version=${process.env.REACT_APP_CI_COMMIT_TAG}`,
			logiMini: `${process.env.PUBLIC_URL}/assets/logo-mini.png?version=${process.env.REACT_APP_CI_COMMIT_TAG}`,
		}
	}
	@computed
	get getTheme() {
		return THEMES.aequor
	}
	@action
	init = async () => {
		// Fetch tenant config first for better performance
		await this.fetchTenantConfig()

		await Promise.allSettled([this.fetchFEConfig()]).catch(console.error)
	}

	/**
	 * Resolves once the app has everything Apollo needs to build its links:
	 * `ready` (localforage rehydrated, set from the constructor) and an `apiUrl`
	 * (set by fetchTenantConfig).
	 *
	 * `when` observes both and fires on the transition, so the caller waits
	 * exactly as long as the slower of the two takes. The previous caller polled
	 * these same two flags every 100ms, which added up to a full tick of dead
	 * time to a boot where nothing else was on screen.
	 */
	whenBootReady = () => when(() => this.ready && Boolean(this.apiUrl))

	@action
	fetchFEConfig = async () => {
		const response = await fetch('/app-config.json')
		const config = await response.json()
		this.feConfig = config || {}
		return config
	}

	@action
	fetchAppConfig = async () => {
		try {
			const response = await apolloClient().query({
				query: GET_APP_INIT_QUERY,
			})
			this.appConfig = response.data?.appInit || {}
		} catch (e) {
			console.error(e)
			throw e
		}
	}

	@action
	fetchPublicAppConfig = async () => {
		try {
			const response = await apolloClient().query({
				query: GET_APP_INIT_QUERY,
				context: { clientName: 'public' },
			})
			this.appPublicConfig = response.data?.appInit || {}
		} catch (e) {
			console.error(e)
			throw e
		}
	}

	@action
	getProfileInfo = async () => {
		try {
			if (authStore.authorization) {
				const data = await apolloClient().query({
					query: GET_USER_INFO_QUERY,
				})

				if (data?.employer) {
					this.profile = data.employer
					this.roles = data.employer?.roles
				}
			}
		} catch (err) {
			console.error(err)
		}
	}

	@action
	fetchTenantConfig = async () => {
		const isDevelopment = window.location.hostname === 'localhost'
		const isGHRorLSR = process.env.REACT_APP_LOCAL_COMPANY_NAME === 'GHR' || process.env.REACT_APP_LOCAL_COMPANY_NAME === 'LSR'
		try {
			if (isDevelopment && isGHRorLSR) {
				this.setApiUrl('https://lsr-backend-qa.opusasia.io')
				// Mark API as ready even for special development case
				if (!window.__OPUS_APP_CONFIG__) {
					window.__OPUS_APP_CONFIG__ = {}
				}
				window.__OPUS_APP_CONFIG__.apiReady = true

				// Auto-load reCAPTCHA for development case
				this.tryLoadReCaptcha()
				return
			}
			const headerConfig = isDevelopment
				? {
						headers: withoutEmptyHeaders({
							'tenant-id': process.env.REACT_APP_DEFAULT_TENANT_ID || '',
							Accept: 'application/json',
						}),
				  }
				: {
						headers: {
							Accept: 'application/json',
						},
				  }

			const response = await axios.get(`${process.env.REACT_APP_PROXY_API_URL}/api/public/v1/connections`, headerConfig)
			if (response?.data) {
				this.setTenantId(response.data.tenantId)
				this.setApiUrl(`https://${response.data.apiDomain}`)
				if (response.data.themeConfig) {
					this.setThemeConfig(response.data.themeConfig)
				} else {
					// Still set a basic config so we know API call completed
					if (!window.__OPUS_APP_CONFIG__) {
						window.__OPUS_APP_CONFIG__ = {}
					}
					window.__OPUS_APP_CONFIG__.apiReady = true

					// Auto-load reCAPTCHA even without themeConfig
					this.tryLoadReCaptcha()
				}
			}
		} catch (e) {
			console.error(e)
		}
	}
}

export const appStore = new AppStore()
